/**
 * SemaforoConfigTab — Configure risk semaphore thresholds from Admin panel.
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, RefreshCw, Info, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_META = [
  {
    key: 'ops_verde',
    label: 'OPS mínimas → VERDE',
    description: 'Número mínimo de OPS en el período para que un centro sea Verde',
    unit: 'OPS',
    color: '#22C55E',
  },
  {
    key: 'ops_amarillo',
    label: 'OPS mínimas → AMARILLO',
    description: 'Número mínimo de OPS para no ser Rojo (entre ops_amarillo y ops_verde = Amarillo)',
    unit: 'OPS',
    color: '#F59E0B',
  },
  {
    key: 'planinc_verde',
    label: '% checklist → VERDE',
    description: 'Porcentaje mínimo de cumplimiento del Plan Inicio Jornada para ser Verde',
    unit: '%',
    color: '#22C55E',
  },
  {
    key: 'planinc_amarillo',
    label: '% checklist → AMARILLO (mín.)',
    description: 'Porcentaje por debajo del cual el centro pasa a Rojo aunque tenga OPS',
    unit: '%',
    color: '#EF4444',
  },
];

export function SemaforoConfigTab() {
  const qc = useQueryClient();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['semaforo-config'],
    queryFn: () => {
      const tok = localStorage.getItem('valoriza_token');
      return axios.get(`${API}/api/admin/semaforo-config`, {
        headers: { Authorization: `Bearer ${tok}` },
      }).then(r => r.data);
    },
  });

  useEffect(() => {
    if (data) {
      setForm({
        ops_verde: data.ops_verde,
        ops_amarillo: data.ops_amarillo,
        planinc_verde: data.planinc_verde,
        planinc_amarillo: data.planinc_amarillo,
      });
    }
  }, [data]);

  const save = async () => {
    // Client-side validation
    if (parseInt(form.ops_amarillo) >= parseInt(form.ops_verde)) {
      toast.error('Las OPS para Amarillo deben ser menores que para Verde');
      return;
    }
    if (parseInt(form.planinc_amarillo) >= parseInt(form.planinc_verde)) {
      toast.error('El % Amarillo debe ser menor que el % Verde');
      return;
    }
    setSaving(true);
    try {
      const tok = localStorage.getItem('valoriza_token');
      await axios.put(`${API}/api/admin/semaforo-config`, form, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      qc.invalidateQueries({ queryKey: ['semaforo-config'] });
      qc.invalidateQueries({ queryKey: ['semaforo'] });
      toast.success('Umbrales del semáforo actualizados');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const reset = async () => {
    setResetting(true);
    try {
      const tok = localStorage.getItem('valoriza_token');
      await axios.delete(`${API}/api/admin/semaforo-config`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      qc.invalidateQueries({ queryKey: ['semaforo-config'] });
      qc.invalidateQueries({ queryKey: ['semaforo'] });
      toast.success('Umbrales restablecidos a valores por defecto');
    } catch (e) {
      toast.error('Error al restablecer');
    } finally { setResetting(false); }
  };

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="semaforo-config-tab">
      {/* Header */}
      <div>
        <h3 className="text-base font-semibold text-white">Umbrales del semáforo de riesgo</h3>
        <p className="text-xs mt-1" style={{ color: 'var(--vz-text-muted)' }}>
          Configura los umbrales que determinan el color (Verde/Amarillo/Rojo) de cada centro
          en el panel del semáforo del Dashboard. Los cambios se aplican en tiempo real.
        </p>
        {data?.is_default && (
          <div className="flex items-center gap-2 mt-2 px-3 py-1.5 rounded text-[11px]"
            style={{ background: 'rgba(74,158,255,0.08)', border: '1px solid rgba(74,158,255,0.2)', color: '#4A9EFF' }}>
            <Info size={11} /> Usando valores por defecto (sin personalización guardada)
          </div>
        )}
      </div>

      {/* Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {FIELD_META.map(({ key, label, description, unit, color }) => (
          <div key={key} className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: color }} />
              {label}
            </Label>
            <div className="relative">
              <Input
                type="number"
                min={0}
                max={unit === '%' ? 100 : 99}
                value={form[key] ?? ''}
                onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                className="h-9 text-sm pr-10 font-mono"
                data-testid={`semaforo-cfg-${key}`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                style={{ color: 'var(--vz-text-muted)' }}>
                {unit}
              </span>
            </div>
            <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>{description}</p>
          </div>
        ))}
      </div>

      {/* Visual preview */}
      <div className="rounded-xl p-4 space-y-2"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--vz-border-subtle)' }}>
        <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>
          Vista previa — cómo se clasificarán los centros:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
          <div className="rounded-lg p-3" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)' }}>
            <div className="font-bold" style={{ color: '#22C55E' }}>● VERDE</div>
            <div style={{ color: 'var(--vz-text-secondary)' }}>
              ≥ {form.ops_verde || '?'} OPS <strong>Y</strong> checklist ≥ {form.planinc_verde || '?'}%
            </div>
          </div>
          <div className="rounded-lg p-3" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)' }}>
            <div className="font-bold" style={{ color: '#F59E0B' }}>● AMARILLO</div>
            <div style={{ color: 'var(--vz-text-secondary)' }}>
              {form.ops_amarillo || '?'}-{(form.ops_verde || 1) - 1} OPS <strong>O</strong> checklist {form.planinc_amarillo || '?'}-{(form.planinc_verde || 1) - 1}%
            </div>
          </div>
          <div className="rounded-lg p-3" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}>
            <div className="font-bold" style={{ color: '#EF4444' }}>● ROJO</div>
            <div style={{ color: 'var(--vz-text-secondary)' }}>
              0 OPS <strong>O</strong> checklist &lt; {form.planinc_amarillo || '?'}%
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          className="gap-1.5 text-xs h-9"
          onClick={save}
          disabled={saving}
          data-testid="semaforo-cfg-save"
        >
          {saving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
          {saving ? 'Guardando...' : 'Guardar umbrales'}
        </Button>
        <Button
          variant="outline"
          className="gap-1.5 text-xs h-9"
          onClick={reset}
          disabled={resetting || data?.is_default}
          data-testid="semaforo-cfg-reset"
        >
          <RotateCcw size={12} /> Restablecer por defecto
        </Button>
      </div>
    </div>
  );
}
